import { coins } from '@klarpay/enums'
import { sendTransaction, signMessage, writeContract } from '@wagmi/core'
import { useCallback, useState } from 'react'
import { isAddress, parseEther } from 'viem'
import { erc20ABI, useAccount, useConnect, useDisconnect, useNetwork, usePublicClient } from 'wagmi'
import { BalanceResult, BlockchainAdapter, BlockchainType, EMPTY_BALANCE } from './types'
import USDT_ABI from '../abi/USDT_ABI.json'

const isProduction = import.meta.env.MODE === 'production'
export function useEthereumAdapter(): BlockchainAdapter {
  const { address, isConnected } = useAccount()
  const { connect, connectors } = useConnect()
  const { disconnect } = useDisconnect()
  const publicClient = usePublicClient()
  const { chain } = useNetwork()
  const [connector, setConnector] = useState<string>()

  const connectCallback = useCallback(
    async (connectorId: string) => {
      setConnector(connectorId)
      const connector = connectors.find(({ ready, id }) => ready && id === connectorId)
      if (!connector) {
        throw new Error('Invalid connector')
      }
      connect({
        connector,
        chainId: isProduction ? 1 : 11155111, // TODO: move this to enums
      })
    },
    [connectors, connect],
  )

  const getBalances = useCallback(
    async (contracts: string[], address: string) => {
      if (!isAddress(address)) {
        throw new Error(`Invalid address ${address}`)
      }
      const tokens = Object.entries(coins)
        .flatMap(([symbol, coin]) =>
          Object.entries(coin.chains).map(([chain, chainData]) => ({
            ...chainData,
            symbol,
            chain,
          })),
        )
        .filter((c) => c.chain === (isProduction ? 'mainnet' : 'sepolia'))
      return Promise.all(
        contracts.map(
          async (token): Promise<BalanceResult> => {
            const tokenSpec = tokens.find((c) => c.contract === token)
            if (!tokenSpec) {
              return EMPTY_BALANCE
            }
            try {
              if (token === 'native') {
                const balance = await publicClient.getBalance({ address })
                return {
                  value: balance,
                  decimals: tokenSpec.decimals,
                  symbol: tokenSpec.symbol,
                  token: 'native',
                  formatted: (Number(balance) / Math.pow(10, tokenSpec.decimals)).toFixed(6),
                }
              } else if (isAddress(token)) {
                const balance = await publicClient.readContract({
                  address: token,
                  abi: erc20ABI,
                  functionName: 'balanceOf',
                  args: [address],
                })
                return {
                  value: balance,
                  decimals: tokenSpec.decimals,
                  symbol: tokenSpec.symbol,
                  token: 'native',
                  formatted: (Number(balance) / Math.pow(10, tokenSpec.decimals)).toFixed(6),
                }
              } else {
                throw new Error(`Unsupported token ${token}`)
              }
            } catch (e) {
              return { ...EMPTY_BALANCE, symbol: tokenSpec?.symbol ?? '-' }
            }
          },
        ),
      )
    },
    [publicClient],
  )

  const sendTransactionCallback = useCallback(
    async (contract, amount, to) => {
      if (!chain) {
        throw new Error('Attempting to call sendTransaction when wallet is not connected')
      }

      if (contract === 'native') {
        const { hash } = await sendTransaction({ to, value: parseEther(String(amount)) })
        return hash
      }

      if (!isAddress(contract)) {
        throw new Error(`Invalid ERC20 contract address ${contract}`)
      }

      if (!isAddress(to)) {
        throw new Error(`Invalid ERC20 contract address ${contract}`)
      }

      const decimals = Object.values(coins)
        .map(({ chains }) => chains[chain.id === 1 ? 'mainnet' : 'sepolia'])
        .find((c) => c?.contract === contract)?.decimals

      if (!decimals) {
        throw new Error(`Calling sendTransaction on invalid contract`)
      }

      const { hash } = await writeContract({
        address: contract,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        abi: contract === coins['USDT'].chains.mainnet?.contract ? (USDT_ABI as any) : erc20ABI,
        functionName: 'transfer',
        args: [to, BigInt(Math.floor(amount * 10 ** decimals))],
      })

      return hash
    },
    [chain],
  )

  return {
    address,
    type: BlockchainType.ETHEREUM,
    hasPublicClient: true,
    isConnected,
    chain: chain
      ? {
          ...chain,
          network: chain?.id === 1 ? 'mainnet' : 'sepolia',
        }
      : undefined,
    connect: connectCallback,
    disconnect,
    signMessage: (message: string) => signMessage({ message }),
    getBalances,
    walletType: connector,
    sendTransaction: sendTransactionCallback,
  }
}
